import { noValidation } from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { useHandleDismiss } from '@post-office/message-templates-common--growth-starburst/hooks/use-handle-dismiss';
import { TwcSampleBanner } from '@atlassian/twc-sample-banner';

import { type MessageTemplateContext } from '../../..';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { getConfluenceOnboardingUrl } from './get-confluence-onboarding-url';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
};

// Custom backend that fetches TCS hostname and TAP site trait to build the Confluence onboarding URL
export const MessageBackend = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext, query, statsig } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId } = message.context;
			const { locale } = orchestrationContext;

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'tenant-id-not-available' })
					.incr();
				throw new Error('Tenant ID not available');
			}

			const isEnabled = statsig.getExperimentValue(
				{ identifiers: { tenantId } },
				{ name: 'twcg-991-jtbd-onboarding-fo', parameter: 'isEnabled', defaultValue: false },
			);

			if (!isEnabled) {
				logger.info(
					{ messageInstanceId, messageTemplateId, triggerId, tenantId },
					'Control cohort - abort hydration',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'control-cohort' })
					.incr();
				throw Error('Control cohort - abort hydration');
			}

			logger.info(
				{ messageTemplateId, messageInstanceId, triggerId, orchestrationContext, query },
				'Hydrating twc-sidenav-banner (in-app-v2/confluence-side-nav)',
			);

			const onboardingUrl = await getConfluenceOnboardingUrl(request, message);

			if (!onboardingUrl) {
				logger.error(
					{ messageTemplateId, messageInstanceId, triggerId },
					'No onboarding URL found for Confluence',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'no-onboarding-url' })
					.incr();
				throw new Error('No onboarding URL found for Confluence');
			}

			request.metrics.counter('twc-sidenav-banner.conf.success.count').incr();

			return {
				...message.context,
				onboardingUrl,
				messageInstanceId,
				locale,
			};
		},
	},
}));

export const MessageFrontend = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			product,
			handleOnDismissed,
			onboardingUrl,
			messageInstanceId,
			locale,
		}: MessageFrontendProps) => {
			const { handleDismiss } = useHandleDismiss({
				onDismissed: handleOnDismissed,
				clickIdentifier: 'twc-sidenav-banner-dismiss',
			});

			return (
				<TwcSampleBanner
					locale={locale}
					product={product}
					onClose={handleDismiss}
					onTryNowClick={handleDismiss}
					href={onboardingUrl}
					messageInstanceId={messageInstanceId}
				/>
			);
		},
	},
});
